// Results screen — legacy report layout: KPI strip → route map → P&L →
// 3PL routes → full-width stacked truck cards.

import { useT } from "../lib/i18n";
import type { OptimizeResponseDTO } from "../types";
import { RouteMap } from "./RouteMap";
import { Alerts, FleetSummary, KpiStrip, Pnl } from "./Summary";
import { ThreePlCard, TruckCard } from "./TruckCard";

export function ResultsView({
  result,
  onNewPlan,
  onFinalize,
}: {
  result: OptimizeResponseDTO;
  onNewPlan: () => void;
  // Opens the dispatch plan for this result; absent when it can't have one.
  onFinalize?: () => void;
}) {
  const t = useT();
  const s = result.summary;
  const isEval = s.solver_status === "evaluate";
  return (
    <main className="results">
      <div className="results-toolbar">
        <div>
          <h2>{isEval ? t("results.titleEval") : t("results.titlePlan")}</h2>
          <div className="meta">
            {isEval ? t("results.evalSubtitle") : ""}
            {t("results.meta", {
              p: s.n_pallets,
              mi: s.total_miles.toFixed(0),
              h: s.total_hours.toFixed(1),
            })}
          </div>
        </div>
        <div className="toolbar-actions">
          {onFinalize && (
            <button className="btn btn-primary" onClick={onFinalize}>
              {t("results.dispatchPlan")}
            </button>
          )}
          <button className="btn btn-secondary" onClick={() => window.print()}>
            {t("results.print")}
          </button>
          <button className="btn btn-ghost" onClick={onNewPlan}>
            {t("results.newPlan")}
          </button>
        </div>
      </div>

      <KpiStrip summary={s} />
      <Alerts summary={s} infeasible={result.infeasible_pallets} />

      <section className="section-block" aria-label="Route map">
        <div className="section-header">
          <h3>{t("results.routeMap")}</h3>
        </div>
        <RouteMap result={result} />
      </section>

      <section className="section-block" aria-label="Profit and loss">
        <div className="section-header">
          <h3>{t("results.pnl")}</h3>
        </div>
        <div className="summary-grid">
          <FleetSummary summary={s} tripCount={result.trucks.length} />
          <Pnl summary={s} />
        </div>
      </section>

      {result.threepl_routes.length > 0 && (
        <section className="section-block" id="threepl-section" aria-label="3PL routes">
          <div className="section-header">
            <h3>{t("results.threepl")}</h3>
          </div>
          {result.threepl_routes.map((r) => (
            <ThreePlCard key={r.route_id} route={r} result={result} />
          ))}
        </section>
      )}

      <section className="section-block" id="trucks-section" aria-label="Truck details">
        <div className="section-header">
          <h3>{t("results.truckDetails")}</h3>
        </div>
        <div className="trucks-stack">
          {result.trucks.map((t) => (
            <TruckCard key={t.slot_id} truck={t} result={result} />
          ))}
        </div>
      </section>
    </main>
  );
}
